import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

// Contexte pour les notifications
const NotificationContext = createContext();

// Hook personnalisé pour utiliser les notifications
export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification doit être utilisé dans un NotificationProvider');
    }
    return context;
};

// Provider pour gérer les notifications
export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const audioContextRef = useRef(null);

    // 🔊 Fonction pour jouer un son
    const playSound = useCallback((type) => {
        try {
            // Créer AudioContext si nécessaire
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }

            const ctx = audioContextRef.current;
            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(ctx.destination);

            // Sons différents selon le type
            switch (type) {
                case 'success':
                    // Son agréable (Do majeur)
                    oscillator.frequency.value = 523.25; // C5
                    oscillator.type = 'sine';
                    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
                    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
                    oscillator.start(ctx.currentTime);
                    oscillator.stop(ctx.currentTime + 0.3);
                    break;

                case 'error':
                    // Son grave et court
                    oscillator.frequency.value = 200;
                    oscillator.type = 'sawtooth';
                    gainNode.gain.setValueAtTime(0.4, ctx.currentTime);
                    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
                    oscillator.start(ctx.currentTime);
                    oscillator.stop(ctx.currentTime + 0.2);
                    break;

                case 'warning':
                    // 🚨 Double bip d'alerte pour warning
                    oscillator.frequency.value = 800;
                    oscillator.type = 'square';
                    gainNode.gain.setValueAtTime(0.5, ctx.currentTime);
                    gainNode.gain.setValueAtTime(0, ctx.currentTime + 0.1);
                    gainNode.gain.setValueAtTime(0.5, ctx.currentTime + 0.15);
                    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
                    oscillator.start(ctx.currentTime);
                    oscillator.stop(ctx.currentTime + 0.4);
                    break;

                case 'info':
                default:
                    // Son neutre
                    oscillator.frequency.value = 440; // A4
                    oscillator.type = 'sine';
                    gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
                    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
                    oscillator.start(ctx.currentTime);
                    oscillator.stop(ctx.currentTime + 0.2);
                    break;
            }
        } catch (error) {
            console.error('Erreur lors de la lecture du son:', error);
        }
    }, []);

    // 📱 Fonction pour détecter iOS
    const isIOS = useCallback(() => {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    }, []);

    // 🔥 Fonction pour quitter le fullscreen avant d'afficher une notification
    const exitFullscreen = useCallback(() => {
        try {
            // Chercher toutes les vidéos
            const videos = document.querySelectorAll('video');

            // iOS : Sortir du fullscreen de la vidéo
            if (isIOS()) {
                videos.forEach(video => {
                    if (video.webkitExitFullscreen) {
                        video.webkitExitFullscreen();
                    }
                });
                if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                }
            }
            // Desktop/Android : Sortir du fullscreen du document
            else if (document.fullscreenElement || document.webkitFullscreenElement) {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                } else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen();
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen();
                }
            }

        } catch (error) {
            console.log('Pas en fullscreen ou erreur:', error);
        }
    }, [isIOS]);

    // ⏸️ Fonction pour mettre en pause toutes les vidéos
    const pauseAllVideos = useCallback(() => {
        const videos = document.querySelectorAll('video');
        let videoPaused = false;

        videos.forEach(video => {
            if (!video.paused && !video.ended) {
                video.pause();
                videoPaused = true;
            }
        });

        return videoPaused;
    }, []);

    // Ajouter une notification
    const addNotification = useCallback((message, type = 'info', duration = 5000) => {
        const id = Date.now() + Math.random();

        // 🔥 NOUVEAU : Quitter le fullscreen pour TOUTES les notifications
        exitFullscreen();

        // 🔊 Jouer le son
        playSound(type);

        // ⏸️ Mettre en pause la vidéo si c'est un warning
        let pausedVideo = false;
        if (type === 'warning') {
            pausedVideo = pauseAllVideos();
            // Warning dure plus longtemps (10 secondes par défaut)
            if (duration === 5000) {
                duration = 10000;
            }
        }

        const notification = {
            id,
            message,
            type, // 'success', 'error', 'warning', 'info'
            duration,
            pausedVideo, // Indique si la vidéo a été mise en pause
            createdAt: Date.now()
        };

        setNotifications(prev => [...prev, notification]);

        // Auto-dismiss après la durée spécifiée
        if (duration > 0) {
            setTimeout(() => {
                removeNotification(id);
            }, duration);
        }

        return id;
    }, [exitFullscreen, playSound, pauseAllVideos]);

    // Supprimer une notification
    const removeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    // Raccourcis pour chaque type
    const success = useCallback((message, duration) => {
        return addNotification(message, 'success', duration);
    }, [addNotification]);

    const error = useCallback((message, duration) => {
        return addNotification(message, 'error', duration);
    }, [addNotification]);

    const warning = useCallback((message, duration) => {
        return addNotification(message, 'warning', duration);
    }, [addNotification]);

    const info = useCallback((message, duration) => {
        return addNotification(message, 'info', duration);
    }, [addNotification]);

    // Supprimer toutes les notifications
    const clearAll = useCallback(() => {
        setNotifications([]);
    }, []);

    const value = {
        notifications,
        addNotification,
        removeNotification,
        success,
        error,
        warning,
        info,
        clearAll
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export default NotificationProvider;